// ============================================================
// dockModel — the Multimodal Command Dock's mode registry (v21).
//
// PURE DATA, exported for tests: the no-dead-ends rule is enforced
// structurally — every mode must resolve to exactly one of:
//   kind 'route'        → navigates to a real, existing route
//   kind 'command_bar'  → hands off to the existing CommandBar
//                         (focus + optional prefill; text/voice
//                         dispatch there is already real)
//   kind 'unavailable'  → carries an honest reason string explaining
//                         exactly why it cannot act yet
// A compliance test asserts this invariant over the whole registry,
// so a decorative dead chip cannot ship.
// ============================================================

export interface DockMode {
  key: string
  label: string
  glyph: string
  kind: 'route' | 'command_bar' | 'unavailable'
  /** What the system will do — shown BEFORE anything happens. */
  preview: string
  /** The permission/gate reality — shown alongside the preview. */
  permissionNote: string
  route?: string
  /** Prefill handed to the CommandBar for command_bar modes. */
  prefill?: string
  unavailableReason?: string
}

export const DOCK_MODES: DockMode[] = [
  {
    key: 'ask',
    label: 'Ask',
    glyph: '?',
    kind: 'command_bar',
    preview: 'Type a question in the command bar; the interpreter answers or routes it — nothing executes without a matching command.',
    permissionNote: 'Read-only interpretation. No gate required.',
  },
  {
    key: 'command',
    label: 'Command',
    glyph: '›',
    kind: 'command_bar',
    preview: 'Dispatch a plain-language command through the existing execute path (text or mic).',
    permissionNote: 'Commands run under your role level; privileged mutations stay gated and audited server-side.',
  },
  {
    key: 'create',
    label: 'Create',
    glyph: '✦',
    kind: 'route',
    preview: 'Open the Creative Studio to compose a brief and run the engine within the draft boundary.',
    permissionNote: 'Draft-only by default; approval gates decide anything beyond draft (minLevel 80).',
    route: '/square/command-center/workspaces/creative',
  },
  {
    key: 'upload',
    label: 'Upload',
    glyph: '⇧',
    kind: 'route',
    preview: 'Open Files to upload into the audited storage buckets.',
    permissionNote: 'Uploads are audited; private buckets only.',
    route: '/square/command-center/workspaces/vault',
  },
  {
    key: 'image',
    label: 'Image',
    glyph: '▣',
    kind: 'unavailable',
    preview: 'Attach a screenshot or image as context for interpretation.',
    permissionNote: '—',
    unavailableReason: 'No image-understanding pipeline is wired yet. Images can be uploaded via Files, but nothing interprets them — saying otherwise would be a fake live state.',
  },
  {
    key: 'url',
    label: 'URL',
    glyph: '⌁',
    kind: 'unavailable',
    preview: 'Attach a link as context for interpretation.',
    permissionNote: '—',
    unavailableReason: 'No server-side URL fetcher exists in this codebase yet — attaching a link would silently do nothing. It lands with the interpretation lane.',
  },
  {
    key: 'voice',
    label: 'Voice',
    glyph: '◍',
    kind: 'command_bar',
    preview: 'Use the mic on the command bar — real Gladia capture → transcription → the same execute path as text.',
    permissionNote: 'Same gates as typed commands. Audio is transcribed server-side; the key never reaches the browser.',
  },
  {
    key: 'meeting',
    label: 'Meeting',
    glyph: '◉',
    kind: 'route',
    preview: 'Open Meeting Mode — controlled Daily rooms with waiting-room concierge, scoped tokens, receipts.',
    permissionNote: 'Fails closed without DAILY_API_KEY. Recording/transcription/external modes stay blocked pending approval wiring.',
    route: '/square/command-center/communications/conference-calls',
  },
  {
    key: 'secure',
    label: 'Secure',
    glyph: '⬒',
    kind: 'route',
    preview: 'Open Secure Access — issue, claim, revoke, and expire scoped access codes.',
    permissionNote: 'Hash-at-rest codes; plaintext shown once at issue. Every action audited.',
    route: '/square/olivium/secure-access',
  },
  {
    key: 'job',
    label: 'Job',
    glyph: '▣',
    kind: 'route',
    preview: 'Open Post Jobs — post work into the Build Now Execute Layer and watch the IDE-style timeline.',
    permissionNote: 'Jobs halt at their approval gate; humans decide gates at minLevel 80.',
    route: '/square/command-center/operations',
  },
]

// ------------------------------------------------------------
// Module landing tiles (v21 "bolder module tiles" + the layout-density
// directive's functional_tiles_needed). Same structural no-dead-ends
// rule: every action is a real route or an honest unavailable reason.
// ------------------------------------------------------------
export interface TileAction {
  label: string
  route?: string
  unavailableReason?: string
}

export interface ModuleTileDef {
  key: string
  title: string
  description: string
  status: 'Live' | 'Ready' | 'Requires setup' | 'Coming later'
  glyph: string
  actions: TileAction[]
}

export const MODULE_TILES: ModuleTileDef[] = [
  {
    key: 'secure-access',
    title: 'Secure Access',
    description: 'Issue, claim, revoke, and expire scoped access codes — hash-at-rest, plaintext shown once, every action audited.',
    status: 'Live',
    glyph: '⬒',
    actions: [
      { label: 'Issue access code', route: '/square/olivium/secure-access' },
      { label: 'Review receipts', route: '/square/olivium/receipts' },
      { label: 'Bind secured item', unavailableReason: 'Artifact binding arrives with the Secure Access Binding Portal (v18 packet, next build lane).' },
    ],
  },
  {
    key: 'project-teams',
    title: 'Project Teams',
    description: 'People, roles, and per-project assignment across the operator lane.',
    status: 'Live',
    glyph: '⛬',
    actions: [
      { label: 'Open people & roles', route: '/square/security/people' },
      { label: 'Create project', route: '/square/projects' },
      { label: 'Open QuamCon thread', route: '/square/comms' },
    ],
  },
  {
    key: 'meeting-mode',
    title: 'Meeting Mode',
    description: 'Controlled Daily rooms with the waiting-room concierge — agenda collection, timeliness relays, human-only admit.',
    status: 'Ready',
    glyph: '◉',
    actions: [
      { label: 'Start a controlled room', route: '/square/command-center/communications/conference-calls' },
      { label: 'Attach meeting to task', unavailableReason: 'Task binding lands with the Secure Access Binding Portal (v18) — meetings become bindable artifacts there.' },
      { label: 'Recorded sessions', unavailableReason: 'Recording is deliberately blocked pending explicit scoped approval wiring — no recordings exist.' },
    ],
  },
  {
    key: 'post-jobs',
    title: 'Post Jobs',
    description: 'Build Now Execute Layer — post work, watch the IDE-style timeline, decide gates, review receipts.',
    status: 'Live',
    glyph: '▣',
    actions: [
      { label: 'Create job', route: '/square/command-center/operations' },
      { label: 'Review approvals', route: '/square/command-center/operations' },
      { label: 'Open receipts', route: '/square/olivium/receipts' },
    ],
  },
  {
    key: 'creative-studio',
    title: 'Creative Studio',
    description: 'Describe it, run the engine (Claude-composed when your key is set), review drafts, decide the gate, save recipes.',
    status: 'Live',
    glyph: '✦',
    actions: [
      { label: 'Create campaign', route: '/square/command-center/workspaces/creative' },
      { label: 'Create flyer', route: '/square/command-center/workspaces/creative' },
      { label: 'Send to marketing review', unavailableReason: 'No live send channel exists in this codebase — deliveries are receipted prepared-not-sent until a send lane is separately authorized.' },
    ],
  },
  {
    key: 'onboarding',
    title: 'Onboarding',
    description: 'Secure invites for team, client, and demo access — one-time codes, audited claims.',
    status: 'Live',
    glyph: '⊛',
    actions: [
      { label: 'Create secure invite', route: '/square/onboarding/secure-invites' },
      { label: 'Create NDA packet', unavailableReason: 'NDA packet generation is not wired yet — it lands with the Onboarding Center lane.' },
      { label: 'Create client shell', unavailableReason: 'Client/demo workspace shells land with the v16 SCFM-09 lane (deferred, documented).' },
    ],
  },
  {
    key: 'quamcon',
    title: 'QuamCon',
    description: 'Conversations, team notifications, and project messages over the real comms substrate.',
    status: 'Live',
    glyph: '◬',
    actions: [
      { label: 'Open conversations', route: '/square/comms' },
      { label: 'Announcements', unavailableReason: 'Broadcast announcements need the v19/v20 Team Roles + QuamCon lane (queued) — no blast channel exists, by design.' },
    ],
  },
  {
    key: 'calendar',
    title: 'Calendar',
    description: 'QCAL-01 planning — describe your day/week/month in plain language, get scored candidate schedules, accept into a prepared handoff. Agents draft; you approve.',
    status: 'Live',
    glyph: '◈',
    actions: [
      { label: 'Plan my time', route: '/square/command-center/calendar' },
      { label: 'Review handoffs', route: '/square/command-center/calendar' },
      { label: 'External calendar sync', unavailableReason: 'No Google/Outlook write is authorized — plans stay inside Square Code until an external-calendar lane is separately approved.' },
    ],
  },
  {
    key: 'knowledge-layer',
    title: 'Knowledge Layer',
    description: 'Governed, versioned, tenant-scoped knowledge updates for agents and teams.',
    status: 'Coming later',
    glyph: '❖',
    actions: [
      { label: 'What will happen here', unavailableReason: 'SCFM-08 (v16 packet) — staged, versioned, approved knowledge updates. Deferred and documented; no backend exists yet, so no tile pretends otherwise.' },
    ],
  },
]
